import { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '../store/auth.store';
import { io, Socket } from 'socket.io-client';
import { FontStyle } from '../store/auth.store';

export default function WidgetPreview() {
  const { user } = useAuthStore();
  const [events, setEvents] = useState<any[]>([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user) return;

    const socket = io('/widget/' + user.id, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('donation:event', (data) => {
      setEvents((prev) => [...prev, { ...data, visible: true, removing: false }]);
      const duration = user.widgetSettings?.displayDuration || 5000;
      setTimeout(() => removeEvent(data.id), duration);
    });

    return () => { socket.disconnect(); };
  }, [user]);

  const removeEvent = (id: string) => {
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, removing: true } : e)));
    setTimeout(() => {
      setEvents((prev) => prev.filter((e) => e.id !== id));
    }, 500);
  };

  const s = user?.widgetSettings;
  const layout = s?.layout || 'image-top';
  const headerFont = s?.headerFont;
  const messageFont = s?.messageFont;

  const getAnimClass = (exiting: boolean) => {
    const type = exiting ? (s?.animationOut || 'fade') : (s?.animationIn || 'fade');
    const prefix = exiting ? 'exit' : 'enter';
    return `${prefix}-${type}`;
  };

  const getFontStyle = (f?: FontStyle): React.CSSProperties => {
    if (!f) return {};
    return {
      fontFamily: `'${f.fontFamily}', sans-serif`,
      fontSize: f.fontSize,
      color: f.color,
      fontWeight: f.bold ? 'bold' : 'normal',
      fontStyle: f.italic ? 'italic' : 'normal',
      textDecoration: f.underline ? 'underline' : 'none',
      textTransform: f.textTransform === 'none' ? 'none' : f.textTransform,
      letterSpacing: f.letterSpacing + 'px',
      wordSpacing: f.wordSpacing + 'px',
      textAlign: f.textAlign,
      textShadow: f.textShadow ? `${f.shadowColor} ${f.shadowOffsetX}px ${f.shadowOffsetY}px ${f.shadowBlur}px` : 'none',
      WebkitTextStroke: f.outline ? `${f.outlineWidth}px ${f.outlineColor}` : undefined,
    };
  };

  const getBgStyle = (): React.CSSProperties => {
    if (!s) return {};
    const hex = s.bgColor || '#1a1a2e';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return {
      background: `rgba(${r},${g},${b},${s.bgOpacity ?? 0.9})`,
      borderRadius: s.borderRadius || 12,
      padding: s.padding || 16,
    };
  };

  const widgetUrl = `${window.location.origin}/widget/${user?.id}`;

  return (
    <div className="widget-preview-page">
      <h1>Widget Preview</h1>
      <p className="page-subtitle">
        Live preview. Go to{' '}
        <a href="/test" style={{ color: '#f59e0b' }}>Test Event</a>{' '}
        to create donations.
      </p>

      <div className="preview-header">
        <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
          <span className="status-dot" />
          {connected ? 'Connected' : 'Disconnected'}
        </div>
        <a href={widgetUrl} target="_blank" rel="noopener noreferrer" className="open-widget-btn">
          Open Widget ↗
        </a>
      </div>

      <div className="preview-container">
        {events.length === 0 ? (
          <div className="empty-preview">
            <div className="empty-icon">🎁</div>
            <p>No donations yet. Create a test event!</p>
          </div>
        ) : (
          events.map((event) => {
            const headerText = (s?.headerTemplate || '{username} - {amount}!')
              .replace('{username}', event.username)
              .replace('{amount}', event.amount + ' ' + (event.currency || ''))
              .replace('{message}', event.message || '');
            const messageText = event.message ? (() => {
              const tpl = s?.messageTemplate || '{message}';
              return tpl.includes('{message}') ? tpl.replace('{message}', event.message) : event.message;
            })() : '';

            const imgUrl = event.avatar || s?.defaultImage;

            return (
              <div key={event.id} className={`donation-notification layout-${layout} ${getAnimClass(event.removing)}`} style={getBgStyle()}>
                {layout === 'image-background' && imgUrl && (
                  <div className="notif-bg-image" style={{ backgroundImage: `url(${imgUrl})` }} />
                )}
                {layout !== 'image-background' && imgUrl && (
                  <div className="notif-image-wrap">
                    <img className="notif-image" src={imgUrl} alt="" style={{
                      width: s?.imageSize || 64,
                      height: s?.imageSize || 64,
                      borderRadius: s?.imageBorderRadius || 50,
                    }} />
                  </div>
                )}
                <div className="notif-content">
                  <div className="notif-header" style={getFontStyle(headerFont)}>
                    {headerText}
                  </div>
                  {messageText && (
                    <div className="notif-message" style={getFontStyle(messageFont)}>
                      {messageText}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <style>{`
        .widget-preview-page h1 { font-size: 28px; margin-bottom: 5px; }
        .page-subtitle { color: var(--text-secondary); font-size: 14px; margin-bottom: 30px; }
        .preview-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .connection-status { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 500; }
        .connection-status.connected { color: var(--success); }
        .connection-status.disconnected { color: var(--error); }
        .status-dot { width: 10px; height: 10px; border-radius: 50%; background: currentColor; animation: pulse 2s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .open-widget-btn { padding: 10px 20px; background: #f59e0b; border-radius: 8px; color: white; font-weight: 600; font-size: 14px; text-decoration: none; }
        .open-widget-btn:hover { background: #d97706; color: white; }
        .preview-container { background: var(--bg-card); border: 2px dashed var(--border); border-radius: 12px; min-height: 400px; padding: 20px; display: flex; flex-direction: column; gap: 15px; position: relative; }
        .empty-preview { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; color: var(--text-secondary); }
        .empty-icon { font-size: 64px; margin-bottom: 15px; }
        
        .donation-notification {
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          max-width: ${s?.maxWidth || 400}px;
          position: relative;
          overflow: hidden;
        }
        .notif-bg-image {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
        }
        .notif-bg-image::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,${1 - (s?.bgOpacity || 0.9)});
        }
        
        /* Layouts */
        .layout-image-top { display: flex; flex-direction: column; align-items: center; text-align: center; }
        .layout-image-top .notif-image-wrap { margin-bottom: 10px; }
        .layout-image-top .notif-content { display: flex; flex-direction: column; align-items: center; }
        .layout-image-bottom { display: flex; flex-direction: column; align-items: center; text-align: center; }
        .layout-image-bottom .notif-content { display: flex; flex-direction: column; align-items: center; }
        .layout-image-bottom .notif-image-wrap { margin-top: 10px; }
        .layout-image-left { display: flex; flex-direction: row; align-items: center; }
        .layout-image-left .notif-image-wrap { margin-right: 12px; }
        .layout-image-left .notif-content { flex: 1; }
        .layout-image-right { display: flex; flex-direction: row; align-items: center; }
        .layout-image-right .notif-content { flex: 1; text-align: right; }
        .layout-image-right .notif-image-wrap { margin-left: 12px; }
        .layout-image-background .notif-content { position: relative; z-index: 1; }
        
        .notif-image { object-fit: cover; flex-shrink: 0; max-width: 100%; height: auto; }
        .notif-content { flex: 1; min-width: 0; }
        .notif-message { margin-top: 6px; word-wrap: break-word; }
        
        /* Animations */
        .enter-fade { animation: fadeIn 0.5s ease-out; }
        .exit-fade { animation: fadeOut 0.5s ease-in forwards; }
        .enter-slide { animation: slideIn 0.5s ease-out; }
        .exit-slide { animation: slideOut 0.5s ease-in forwards; }
        .enter-bounce { animation: bounceIn 0.5s ease-out; }
        .exit-bounce { animation: bounceOut 0.5s ease-in forwards; }
        .enter-zoom { animation: zoomIn 0.5s ease-out; }
        .exit-zoom { animation: zoomOut 0.5s ease-in forwards; }
        .enter-flip { animation: flipIn 0.5s ease-out; }
        .exit-flip { animation: flipOut 0.5s ease-in forwards; }
        .enter-none { animation: none; }
        .exit-none { animation: fadeOut 0.5s ease-in forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(-100%); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideOut { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(-100%); } }
        @keyframes bounceIn { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes bounceOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.3); } }
        @keyframes zoomIn { from { opacity: 0; transform: scale(0); } to { opacity: 1; transform: scale(1); } }
        @keyframes zoomOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0); } }
        @keyframes flipIn { from { opacity: 0; transform: perspective(400px) rotateY(90deg); } to { opacity: 1; transform: perspective(400px) rotateY(0); } }
        @keyframes flipOut { from { opacity: 1; transform: perspective(400px) rotateY(0); } to { opacity: 0; transform: perspective(400px) rotateY(90deg); } }
      `}</style>
    </div>
  );
}
